import React, { useContext, useRef, useState, useMemo } from 'react';
import { Column, TableInstance } from 'react-table';
import { ResponseError } from '@nintendo/forms';
import { Button } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { RoleUtils } from '@galaxy/lorule-react-commons';
import DataTable from '../../common/datatables/DataTable';
import { getPendingProjectCommitments } from '../../../api/projectBalanceApi';
import { PendingProjectCommitmentItem } from '../../../api/projectBalanceApiTypes';
import ConfigContext from '../../../contexts/ConfigContext';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import Spinner from '../../common/Spinner';
import { exportPendingCommitmentsList } from '../../common/listUtils';
import { formatCurrency, renderDateCell } from '../../../utils/displayUtils';

interface PendingCommitmentsProps {
    amId: number;
    amNumber: number;
    total: number | null;
}

const PendingCommitments = (props: PendingCommitmentsProps) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const { amId, amNumber } = props;

    const {
        value: pendingCommitments,
        loading,
        error,
    } = useAsyncWithState(
        () =>
            getPendingProjectCommitments(
                config,
                amId,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            ),
        [] as PendingProjectCommitmentItem[],
    );

    const tableInstanceRef = useRef<TableInstance<PendingProjectCommitmentItem> | null>(null);
    const [tableRefReady, setTableRefReady] = useState(false);

    const columns = React.useMemo(
        () => [
            {
                Header: 'Date',
                accessor: 'date',
                Cell: renderDateCell,
            },
            {
                Header: 'Source',
                accessor: 'source',
                width: null,
            },
            {
                Header: 'Transaction #',
                accessor: 'commitmentNumber',
                width: null,
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                Cell: (cellProps: { value: number }) =>
                    useMemo(() => {
                        const absValue = Math.abs(cellProps.value);
                        const negativeValue = `(${formatCurrency(absValue, 2)})`;
                        return (
                            <span className="desktop-float-right pr-3">
                                {cellProps.value < 0 ? negativeValue : formatCurrency(absValue, 2)}
                            </span>
                        );
                    }, []),
                width: 150,
            },
        ],
        [],
    );

    return loading ? (
        <Spinner />
    ) : (
        <>
            <ResponseError errorMessage={error} />
            <div className="col-12 py-2 border-bottom">
                <div className="row">
                    <div className="col-2 col-overview-label">Total Pending Commitments</div>
                    <div className="col-2 col-overview-label">
                        {props.total != null && formatCurrency(props.total)}
                    </div>
                    <div className="col-8 text-right">
                        <Button
                            variant="outline-primary"
                            disabled={loading || !tableRefReady || pendingCommitments.length <= 0}
                            onClick={() => {
                                if (tableInstanceRef.current) {
                                    exportPendingCommitmentsList(
                                        tableInstanceRef.current.rows.map((row) => row.original),
                                        `Project ${amNumber} Pending Commitments.csv`,
                                    );
                                }
                            }}
                            size="sm"
                        >
                            <i className="fas fa-file-export" /> Export CSV
                        </Button>
                    </div>
                </div>
            </div>
            <div className="react-table p-2">
                <DataTable<PendingProjectCommitmentItem>
                    columns={columns as Column<PendingProjectCommitmentItem>[]}
                    data={pendingCommitments}
                    noGlobalFilter
                    getInstanceCallback={(tableInstance) => {
                        if (tableInstanceRef) {
                            tableInstanceRef.current = tableInstance;
                            setTableRefReady(true);
                        }
                    }}
                />
            </div>
        </>
    );
};

export default PendingCommitments;
